import { useState } from 'react';
import type { LinkResponse } from '@upm/domain';
import { fmtDate } from '../lib/format';
import { Banner, Button } from '../ui';

/**
 * A firm link is shown exactly once. When the invite wasn't emailed (staff chose to send it themselves, or
 * sending failed), the plain-text draft is laid out to copy into a new Outlook email field by field.
 */
export function LinkResult({ result, email, systemMailbox }: { result: LinkResponse; email: string | null; systemMailbox?: string }) {
  const [copied, setCopied] = useState<string | null>(null);
  const copy = async (key: string, text: string, fallbackId: string) => {
    try { await navigator.clipboard.writeText(text); setCopied(key); }
    catch { (document.getElementById(fallbackId) as HTMLInputElement | HTMLTextAreaElement | null)?.select(); }
  };
  const once = `This link is shown once and can’t be retrieved later. It expires ${fmtDate(result.expiresAt)}. To send a fresh one, use Reissue link on the firm’s review.`;

  if (!result.emailed && result.draft) {
    const d = result.draft;
    return (
      <div className="flex flex-col gap-4">
        {email
          ? <Banner tone="warn">The email didn’t send. Paste these into a new Outlook email instead.</Banner>
          : <p className="m-0 text-sm text-ink-2">Paste these into a new Outlook email. The firm shows as “Waiting on the firm” until they open the link.</p>}
        <CopyField id="draft-to" label="To" value={d.to} copied={copied === 'to'} onCopy={() => void copy('to', d.to, 'draft-to')} />
        <CopyField id="draft-subject" label="Subject" value={d.subject} copied={copied === 'subject'} onCopy={() => void copy('subject', d.subject, 'draft-subject')} />
        <CopyField id="one-time-link" label="Onboarding link" value={result.link} mono copied={copied === 'link'} onCopy={() => void copy('link', result.link, 'one-time-link')} />
        <div className="flex flex-col gap-1.5">
          <div className="flex items-end justify-between gap-2">
            <label htmlFor="draft-message" className="text-sm font-semibold">Message <span className="font-normal text-muted">(plain text, link included)</span></label>
            <Button variant="outline" size="sm" className="min-w-[92px]" onClick={() => void copy('message', d.text, 'draft-message')}>{copied === 'message' ? 'Copied' : 'Copy'}</Button>
          </div>
          <textarea id="draft-message" readOnly rows={12} value={d.text} onFocus={(e) => e.target.select()}
            className="box-border w-full resize-y rounded-[10px] border border-line bg-panel px-3.5 py-3 text-sm leading-[1.55] text-ink" />
        </div>
        <div aria-live="polite" className="sr-only">{copied ? 'Copied' : ''}</div>
        <p className="m-0 text-[13px] text-muted">{once}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {result.emailed
        ? <Banner tone="success">
            Invite emailed{email ? ` to ${email}` : ''}{result.emailedVia === 'no_reply'
              ? ` from ${systemMailbox ?? 'no-reply@vertekal.com'}. Replies come to you.`
              : ' from your Outlook.'}
          </Banner>
        : <Banner tone="warn">Not emailed. Copy the link and send it from your own email.</Banner>}
      <CopyField id="one-time-link" label="Firm link" value={result.link} mono copied={copied === 'link'} onCopy={() => void copy('link', result.link, 'one-time-link')} />
      <div aria-live="polite" className="sr-only">{copied ? 'Link copied' : ''}</div>
      <p className="m-0 text-[13px] text-muted">{once}</p>
    </div>
  );
}

function CopyField({ id, label, value, mono, copied, onCopy }: { id: string; label: string; value: string; mono?: boolean; copied: boolean; onCopy: () => void }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-semibold">{label}</label>
      <div className="flex flex-wrap gap-2">
        <input id={id} readOnly value={value} className={`input min-w-0 flex-[1_1_260px] ${mono ? 'font-mono text-[13px]' : ''}`} onFocus={(e) => e.target.select()} />
        <Button variant="outline" className="min-w-[92px]" onClick={onCopy}>{copied ? 'Copied' : 'Copy'}</Button>
      </div>
    </div>
  );
}
